import { TicketSummary } from "@/lib/types/database";
import type {
  TicketPriority,
  TicketStatus,
  TicketCategory,
} from "@/features/tickets/types/ticket-enums";
import type { AssignedUserData, ContactData, RawTicketData, ProcessedTicketData } from "./ticket-types";

export type { AssignedUserData, ContactData, RawTicketData, ProcessedTicketData };

const TICKET_STATUSES: readonly TicketStatus[] = [
  "new",
  "assigned",
  "in_progress",
  "waiting_for_customer",
  "waiting_for_vendor",
  "resolved",
  "closed",
  "cancelled",
];
const TICKET_PRIORITIES: readonly TicketPriority[] = [
  "low",
  "medium",
  "high",
  "critical",
  "urgent",
];
const TICKET_CATEGORIES: readonly TicketCategory[] = [
  "hardware",
  "software",
  "network",
  "security",
  "backup",
  "cloud",
  "email",
  "phone",
  "printer",
  "other",
];

function toTicketStatus(value: string): TicketStatus {
  return (TICKET_STATUSES as readonly string[]).includes(value)
    ? (value as TicketStatus)
    : "new";
}

function toTicketPriority(value: string): TicketPriority {
  return (TICKET_PRIORITIES as readonly string[]).includes(value)
    ? (value as TicketPriority)
    : "medium";
}

function toTicketCategory(value: string | undefined): TicketCategory {
  return value && (TICKET_CATEGORIES as readonly string[]).includes(value)
    ? (value as TicketCategory)
    : "other";
}

export function getAssignedUserInfo(ticket: {
  assigned_to?: string | null;
  assigned_user?: AssignedUserData;
  assigned_to_name?: string;
  assigned_to_email?: string;
}): {
  displayName: string;
  assignedToId: string | null;
  hasAssignment: boolean;
} {
  if (!ticket.assigned_to && !ticket.assigned_user) {
    return {
      displayName: "Unassigned",
      assignedToId: null,
      hasAssignment: false,
    };
  }

  const assignedToId = ticket.assigned_to || ticket.assigned_user?.id || null;

  if (ticket.assigned_to_name && ticket.assigned_to_name !== "Unassigned") {
    return {
      displayName: ticket.assigned_to_name,
      assignedToId,
      hasAssignment: true,
    };
  }

  if (ticket.assigned_user) {
    const { first_name, last_name, email } = ticket.assigned_user;

    if (first_name || last_name) {
      const fullName = `${first_name || ""} ${last_name || ""}`.trim();
      return {
        displayName: fullName,
        assignedToId,
        hasAssignment: true,
      };
    }

    if (email) {
      return {
        displayName: email,
        assignedToId,
        hasAssignment: true,
      };
    }
  }

  if (ticket.assigned_to_email && ticket.assigned_to_email !== "Unassigned") {
    return {
      displayName: ticket.assigned_to_email,
      assignedToId,
      hasAssignment: true,
    };
  }

  return {
    displayName: "Unassigned",
    assignedToId,
    hasAssignment: false,
  };
}

export function getClientInfo(ticket: {
  contact_id?: string;
  contacts?: ContactData;
  client_name?: string;
  client_email?: string;
  company_name?: string;
  contact_name?: string;
}): {
  clientName: string;
  clientEmail?: string;
  hasClient: boolean;
} {
  if (ticket.client_name && ticket.client_name !== "Unknown Client") {
    return {
      clientName: ticket.client_name,
      clientEmail: ticket.client_email,
      hasClient: true,
    };
  }

  if (ticket.company_name || ticket.contact_name) {
    return {
      clientName: ticket.company_name || ticket.contact_name || "Unknown Client",
      clientEmail: undefined,
      hasClient: true,
    };
  }

  if (ticket.contacts) {
    const { company_name, name, email } = ticket.contacts;

    if (company_name) {
      return {
        clientName: company_name,
        clientEmail: email,
        hasClient: true,
      };
    }

    if (name) {
      return {
        clientName: name,
        clientEmail: email,
        hasClient: true,
      };
    }
  }

  if (ticket.client_email) {
    return {
      clientName: ticket.client_email,
      clientEmail: ticket.client_email,
      hasClient: true,
    };
  }

  return {
    clientName: "Unknown Client",
    clientEmail: undefined,
    hasClient: false,
  };
}

export function formatTicketNumber(ticketNumber?: number | null): string | undefined {
  if (ticketNumber === undefined || ticketNumber === null) {
    return undefined;
  }
  return `WTC-${String(ticketNumber).padStart(6, "0")}`;
}

export function processTicketData(ticket: RawTicketData): ProcessedTicketData {
  const assignedUserInfo = getAssignedUserInfo(ticket);
  const clientInfo = getClientInfo(ticket);
  const formattedTicketNumber = formatTicketNumber(ticket.ticket_number);

  return {
    ...ticket,
    assigned_to_name: assignedUserInfo.displayName,
    client_name: clientInfo.clientName,
    client_email: clientInfo.clientEmail,
    formatted_ticket_number: formattedTicketNumber,
  };
}

export function processTicketsData(tickets: RawTicketData[]): ProcessedTicketData[] {
  return tickets.map(processTicketData);
}

export function convertToTicketSummary(ticket: ProcessedTicketData): TicketSummary {
  return {
    id: ticket.id,
    ticket_number:
      typeof ticket.ticket_number === "number"
        ? ticket.ticket_number
        : ticket.ticket_number
          ? Number(ticket.ticket_number)
          : 0,
    formatted_ticket_number: ticket.formatted_ticket_number,
    title: ticket.title,
    status: toTicketStatus(ticket.status),
    priority: toTicketPriority(ticket.priority),
    category: toTicketCategory(ticket.category),
    created_at: ticket.created_at,
    due_date: undefined,
    actual_hours: 0,
    billable_hours: 0,
    total_cost: 0,
    total_billed: 0,
    contact_name: ticket.client_name || "",
    contact_company: undefined,
    assigned_to_email: undefined,
    created_by_email: undefined,
    assigned_to_name: ticket.assigned_to_name,
    tags: undefined,
  };
}

export function convertToTicketSummaryArray(tickets: ProcessedTicketData[]): TicketSummary[] {
  return tickets.map(convertToTicketSummary);
}
